import { FormattedMessage, useIntl } from 'react-intl'
import { Grid } from '@mui/material'
import FormSectionContainer from 'components/layout/FormSectionContainer'
import { useState, useEffect } from 'react'
import { styled } from 'styled-components'
import { useAppDispatch } from 'store'
import { getChallengeImageV2 } from 'store/userChallenge/thunks'
import { ChallengeImageType, UserChallenge } from 'api/types'

type ImageField = {
  name: string
  label: string
  suggestedWidth: number
  suggestedHeight: number
  maxFileSizeMB: number
  circular?: boolean
  logoWidth?: number
}

interface Props {
  challenge: UserChallenge
}

export default function ChallengeReadOnlyPhase4({ challenge }: Props): JSX.Element {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const [processedImage, setProcessedImage] = useState<string | null>(null)

  const imageFields: ImageField[] = [
    {
      name: ChallengeImageType.BACKGROUND,
      label: intl.formatMessage({ id: 'challenges.backgroundImage' }),
      suggestedWidth: 1125,
      suggestedHeight: 861,
      maxFileSizeMB: 4.0,
    },
  ]

  useEffect(() => {
    const fetchImage = async () => {
      if (challenge.media?.coverImage?.updatedAt && challenge.challengeId) {
        try {
          const res = await dispatch(
            getChallengeImageV2({
              challengeId: challenge.challengeId,
              challengeImageType: 'CHALLENGE_IMAGE_TYPE_COVER',
            }),
          ).unwrap()
          setProcessedImage(res.data?.imageUrl ?? null)
        } catch (err) {
          console.error(err)
        }
      }
    }

    fetchImage()
  }, [])

  const renderImagePreview = (imageField: ImageField) => {
    const imageUrl = processedImage
    return (
      <ImagePreview
        $hasImage={!!imageUrl}
        $circular={imageField.circular}
        $aspectRatio={imageField.suggestedWidth / imageField.suggestedHeight}
      >
        {imageUrl ? (
          <>
            <img src={imageUrl} />
          </>
        ) : (
          intl.formatMessage({ id: 'challenges.preview' }) || 'Preview'
        )}
      </ImagePreview>
    )
  }

  return (
    <>
      <FormSectionContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <h2>
              <FormattedMessage id="challenges.brandingHeader" />
            </h2>
          </Grid>
          {imageFields.map((imageField) => (
            <Grid item xs={4} key={imageField.name}>
              <div>
                <ImagePreviewContainer>{renderImagePreview(imageField)}</ImagePreviewContainer>
                <h3>{imageField.label}</h3>
                <p style={{ color: '#8A8A8E', fontSize: 14, marginBottom: 16 }}>
                  <FormattedMessage id="branding.fileTypes" />: JPG, JPEG, PNG
                  <br />
                  <FormattedMessage id="branding.recommendedDimensions" />: {imageField.suggestedWidth}x
                  {imageField.suggestedHeight}
                </p>
              </div>
            </Grid>
          ))}
        </Grid>
      </FormSectionContainer>
    </>
  )
}

const ImagePreviewContainer = styled.div`
  display: flex;
  height: 100%;
  margin: 1rem 0;
`

const ImagePreview = styled.div<{ $hasImage: boolean; $circular?: boolean; $aspectRatio: number }>`
  border: 2px dashed ${(props) => (props.$hasImage ? '#373D40' : '#BBBBBD')};
  border-radius: ${(props) => (props.$circular ? '50%' : '16px')};
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 4px;
  box-sizing: content-box;
  color: #8a8a8e;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 15px;
  aspect-ratio: ${(props) => props.$aspectRatio};

  & img {
    height: 100%;
    border-radius: ${(props) => (props.$circular ? '50%' : '12px')};
  }
`
